<template>
  <ElTooltip placement="top" :content="$t('MORE_OPTIONS')">
    <LbDropdown class="more-options-button" :items="options">
      <ElButton plain size="mini" :loading="loading">
        <i class="el-icon-more" />
      </ElButton>
    </LbDropdown>
  </ElTooltip>
</template>

<script>
import downloadFileWithUrl from '@/utils/downloadFileWithUrl';
import LbDropdown from '@@/common/LbDropdown';
import AppPackageDetailsModal from '@@/app/common/AppPackageDetailsModal';
import packageName from '@/mixins/packageName';
import { Release } from '@/api/api';
import { mapActions, mapMutations } from 'vuex';
import { REMOVE_RELEASE_PACKAGE, RELEASES } from '@/store/types';

export default {
  name: 'MoreOptionsButton',
  components: {
    LbDropdown,
  },
  mixins: [packageName],
  props: {
    packageItem: {
      type: Object,
      default: null,
    },
    release: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    options() {
      const options = [
        {
          title: this.$t('APP.RELEASES.DETAIL_PACKAGE_BTN'),
          handler: () => this.openPackageDetailsModal(),
        },
        {
          title: this.$t('APP.RELEASES.DOWNLOAD_PACKAGE'),
          disabled: !this.packageItem.isIndependentlyInstallable,
          handler: () => downloadFileWithUrl(this.packageItem.apkLink),
        },
      ];

      if (this.canDeletePackage) {
        options.push({
          title: this.$t('APP.RELEASES.REMOVE_PACKAGE'),
          handler: () => this.getConfirmAndDelete(),
        });
      }

      return options;
    },
    hasBundle() {
      return this.release.bundle;
    },
    canDeletePackage() {
      const isNotRemovedPakage = this.packageItem.publishState !== 'S';
      const canEditPackagesOfRelease = this.release?.actions?.editPackage;
      return canEditPackagesOfRelease && isNotRemovedPakage;
    },
  },
  methods: {
    ...mapMutations({
      removeReleasePackage: REMOVE_RELEASE_PACKAGE,
    }),
    ...mapActions({ fetchReleases: RELEASES }),

    getConfirmAndDelete() {
      const message = this.$t('APP.RELEASES.REMOVE_PACKAGE_CONFIRMATION');
      const title = this.$t('WARNING');
      const confirmButtonText = this.$t('DELETE_IT');
      const cancelButtonText = this.$t('CANCEL');
      const cancelButtonClass = 'el-button--text is-plain';
      const confirmButtonClass = 'el-button--danger';

      return this.$confirm(message, title, {
        confirmButtonText,
        cancelButtonText,
        cancelButtonClass,
        confirmButtonClass,
        type: 'warning',
      })
        .then(() => {
          this.removePackageFromRelease();
        })
        .catch(() => {
          // As there is nothing to do with this,
          // I only want to specify a function to handle
          // an unhandled promise rejection
        });
    },

    removePackageFromRelease() {
      this.loading = true;
      return Release.removePackageFromRelease({
        packageName: this.packageName,
        package: this.packageItem,
        releaseId: this.release.token,
      })
        .then(message => {
          this.$message.success(message);
          this.removePackageFromLocalRelease();
        })
        .catch(({ message }) => {
          this.$message.error(message);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    removePackageFromLocalRelease() {
      this.removeReleasePackage({
        releaseId: this.release.token,
        packageToken: this.packageItem.token,
      });
      this.fetchReleases(this.packageName);
    },

    openPackageDetailsModal() {
      const packageItem = this.packageItem;

      this.$modal.open({
        title: this.$t('APP.RELEASES.DETAIL_MODAL_TITLE'),
        component: AppPackageDetailsModal,
        props: { packageItem },
        width: '35%',
      });
    },
  },
};
</script>
